import React, { useCallback, useMemo, useRef, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { CreatableSelect } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import type { ThirdPartyStatus } from '../../../../../../common/types';
import { useThirdPartyState } from '../../../../../../controllers/import-state';
import { useThirdPartyStatusMappingState } from '../../../../../../controllers/import-state/selectors/third-party-status-mapping-state';
import { useUIState } from '../../../../../../controllers/ui-state';
import { AddStatusModal } from './add-status-modal';
import messages from './messages';
import type { StatusOption } from './types';
import { useStatusOptions } from './utils';

type StatusSelectorProps = {
	selectedStatus?: ThirdPartyStatus | null;
};

export const StatusSelector = ({ selectedStatus }: StatusSelectorProps) => {
	const [, { setThirdPartyStatusMapping }] = useThirdPartyStatusMappingState();
	const [{ thirdPartyStatusMapping }] = useThirdPartyState();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [newStatusName, setNewStatusName] = useState('');
	const focusRef = useRef(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [, { setUIState }] = useUIState();

	const formattedOptions = useStatusOptions(
		(thirdPartyStatusMapping?.statusOptions ?? []).concat(
			thirdPartyStatusMapping?.availableJiraStatuses ?? [],
		),
	);

	const statusValue = useMemo(() => {
		const matchedStatus = formattedOptions.filter(
			(statusOptions) =>
				statusOptions.value.toLocaleLowerCase() ===
				selectedStatus?.mappedStatusName.toLocaleLowerCase(),
		);
		return matchedStatus.length > 0 ? matchedStatus[0] : null;
	}, [formattedOptions, selectedStatus?.mappedStatusName]);

	const updateOptionsOrder = useCallback(
		(options: StatusOption[], currentStatus: StatusOption) => {
			const isCurrentInStatusOptions = thirdPartyStatusMapping?.statusOptions.find(
				(status) => status.name.toLocaleLowerCase() === currentStatus.value.toLocaleLowerCase(),
			);

			if (!isCurrentInStatusOptions) {
				const updatedOptions = options.filter(
					(option) => option.value.toLocaleLowerCase() !== currentStatus.value.toLocaleLowerCase(),
				);
				updatedOptions.unshift(currentStatus);
				return updatedOptions;
			}

			return options;
		},
		[thirdPartyStatusMapping?.statusOptions],
	);

	const getLozenge = useCallback(
		(option: StatusOption) => <Lozenge appearance={option.appearance}>{option.label}</Lozenge>,
		[],
	);

	const handleCreateLabel = (inputValue: string) => (
		<>
			{inputValue} <Text size="small">({formatMessage(messages.addStatus)})</Text>
		</>
	);
	const handleModalClose = useCallback(() => setIsModalOpen(false), []);

	const handleOnChange = useCallback(
		(option: StatusOption | null) => {
			if (option && selectedStatus) {
				setThirdPartyStatusMapping({
					sourceStatusName: selectedStatus.sourceStatusName,
					mappedStatusName: option.value,
					statusCategory: option.statusCategory,
				});

				setUIState({ hasUnsavedStatusMappingChanges: true });
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'projectStatusSelector',
				});

				fireUIAnalytics(analyticsEvent, 'statusItem changed', {
					selectedStatus: option.value,
					sourceStatus: selectedStatus.sourceStatusName,
				});
			}
		},
		[createAnalyticsEvent, selectedStatus, setThirdPartyStatusMapping, setUIState],
	);

	const onStatusCreate = (inputValue: string) => {
		setNewStatusName(inputValue);
		setIsModalOpen(true);
	};

	return (
		<Box
			xcss={selectWrapperStyle}
			testId="project-create-importer-upload-flow.ui.third-party-review-screen.status-mapping.status-mapping-content.status-table.status-selector.menu"
		>
			<CreatableSelect
				ref={focusRef}
				inputId={`status-select-${selectedStatus?.sourceStatusName}`}
				placeholder={formatMessage(messages.statusMappingPlaceholder)}
				options={statusValue ? updateOptionsOrder(formattedOptions, statusValue) : formattedOptions}
				value={statusValue}
				formatOptionLabel={getLozenge}
				onCreateOption={onStatusCreate}
				formatCreateLabel={handleCreateLabel}
				onChange={handleOnChange}
				aria-label={formatMessage(messages.statusMapping)}
			/>
			<ModalTransition>
				{isModalOpen && (
					<AddStatusModal
						onClose={handleModalClose}
						statusCategory={(selectedStatus && selectedStatus.statusCategory) ?? undefined}
						sourceStatusName={(selectedStatus && selectedStatus.sourceStatusName) ?? undefined}
						focusRef={focusRef}
						newStatusName={newStatusName}
					/>
				)}
			</ModalTransition>
		</Box>
	);
};

const selectWrapperStyle = xcss({
	width: '275px',
});
